import { Link } from "react-router-dom";
import {
  GraduationCap,
  ShieldCheck,
  Sparkles,
  Coins,
  Flame,
  Trophy,
  ShoppingBag,
  Star,
  ArrowRight,
  Play,
  Users,
  Globe,
  Award,
  CheckCircle2,
  Hash,
  BookOpen,
  Cpu,
} from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";

const HERO_BG = "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1800";

const TRUST_LOGOS = [
  "Polygon", "Ethereum Foundation", "AWS", "TMB Global Network", "Blockchain.ai", "Qfinity Media",
];

const CATEGORIES = [
  {
    title: "Blockchain & Web3",
    courses: "12 programmes",
    image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=900",
    accent: "From Bitcoin protocol design to DeFi & DAO governance.",
    icon: Hash,
  },
  {
    title: "Artificial Intelligence",
    courses: "9 programmes",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=900",
    accent: "Production LLMs, agents, RAG, evaluation and shipping AI products.",
    icon: Cpu,
  },
  {
    title: "Smart Contracts",
    courses: "6 programmes",
    image: "https://images.unsplash.com/photo-1639322537228-f710d846310a?w=900",
    accent: "Solidity, Foundry, security audits and L2 deployment.",
    icon: ShieldCheck,
  },
  {
    title: "Decentralised Finance",
    courses: "5 programmes",
    image: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=900",
    accent: "AMMs, lending, stablecoins, perpetuals and risk on-chain.",
    icon: Coins,
  },
];

const FEATURED_COURSES = [
  {
    title: "Blockchain Fundamentals",
    instructor: "Prof. Daniel Okafor",
    rating: 4.9,
    students: 2410,
    duration: "6 months",
    level: "Beginner",
    image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=900",
    price: "$295",
    badge: "Bestseller",
  },
  {
    title: "Smart Contracts with Solidity",
    instructor: "Prof. Daniel Okafor",
    rating: 4.8,
    students: 1820,
    duration: "12 months",
    level: "Intermediate",
    image: "https://images.unsplash.com/photo-1639322537228-f710d846310a?w=900",
    price: "$1,195",
    badge: "Diploma",
  },
  {
    title: "AI & Prompt Engineering",
    instructor: "Prof. Daniel Okafor",
    rating: 4.9,
    students: 3140,
    duration: "6 months",
    level: "Beginner",
    image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=900",
    price: "$325",
    badge: "Trending",
  },
  {
    title: "BSc Applied AI",
    instructor: "Prof. Daniel Okafor",
    rating: 4.9,
    students: 540,
    duration: "24 months",
    level: "Degree",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=900",
    price: "$5,095",
    badge: "Degree",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "Mowanzi's blockchain certificate landed me three job offers in a month. The verifiable credential made my CV stand out.",
    name: "Thandiwe Mokoena",
    role: "Junior Smart Contract Engineer · Cape Town",
    avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200",
  },
  {
    quote:
      "I earned 12,000 MWZ during my Diploma and used it to buy the Ledger Nano X. Best learning incentive I've experienced.",
    name: "Sipho Dlamini",
    role: "DeFi Researcher · Johannesburg",
    avatar: "https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?w=200",
  },
  {
    quote:
      "The AI Tutor (Claude + GPT switching) helped me through every Solidity assignment. It's like having a senior dev on call.",
    name: "Amara Okafor",
    role: "BSc Blockchain Engineering · Lagos",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* ==================== HERO ==================== */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-hero-gradient" />
        <div className="bg-grid-dense absolute inset-0 opacity-30" />

        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 py-24 lg:grid-cols-12 lg:py-32">
          <div className="lg:col-span-7">
            <div className="chip-gold mb-6 inline-flex" data-testid="hero-chip">
              <Sparkles className="h-3.5 w-3.5" />
              Pan-African Academic Blockchain Network
            </div>
            <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Learn the skills.
              <br />
              <span className="gold-text-gradient">Earn the credential.</span>
              <br />
              Own your future.
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-white/80">
              Mowanzi Academy combines world-class courses in Blockchain & AI
              with verifiable on-chain credentials, an AI tutor, and the
              <span className="font-semibold text-white"> Mowanzi (MWZ) </span>
              reward currency you earn as you learn.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/login">
                <Button
                  size="lg"
                  className="btn-gold rounded-full px-7 py-6 text-base font-semibold"
                  data-testid="hero-cta-start"
                >
                  Start learning — claim 500 MWZ
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/courses">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full border-2 border-white/30 bg-white/5 px-7 py-6 text-base font-semibold text-white backdrop-blur-sm hover:bg-white/15 hover:text-white"
                  data-testid="hero-cta-browse"
                >
                  <Play className="mr-2 h-4 w-4" />
                  Browse catalogue
                </Button>
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 border-t border-white/15 pt-7 sm:max-w-xl">
              <Stat n="12,400+" label="Active learners" />
              <Stat n="32" label="Programmes" />
              <Stat n="98%" label="Job placement" />
            </div>
          </div>

          {/* Right column — featured cert preview */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-6 -right-6 hidden lg:block">
                <div className="chip-gold flex items-center gap-2 backdrop-blur-md">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  NFT-backed · Polygon
                </div>
              </div>

              <div className="cert-grain rounded-2xl border border-white/20 bg-white/95 p-7 shadow-2xl backdrop-blur-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      Certificate of Completion
                    </div>
                    <div className="font-display mt-1 text-2xl font-bold tracking-tight text-foreground">
                      Blockchain Fundamentals
                    </div>
                  </div>
                  <div className="bg-gold-gradient flex h-12 w-12 items-center justify-center rounded-xl">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                </div>

                <div className="mt-5 flex items-center gap-3">
                  <img
                    src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200"
                    alt="recipient"
                    className="h-12 w-12 rounded-full border-2 border-primary object-cover"
                  />
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      Awarded to
                    </div>
                    <div className="font-semibold text-foreground">Aria Mokoena</div>
                  </div>
                </div>

                <div className="mt-6 break-all rounded-md border border-border bg-muted px-3 py-2 font-mono text-[10px] leading-relaxed text-foreground/70">
                  <span className="text-muted-foreground">cert_hash:</span>{" "}
                  0x9f3a7d1c4b8e6a2f5d9c1e8b4a7f2d6c…
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Block #51,284,917
                  </div>
                  <div className="chip">
                    <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                    Verified
                  </div>
                </div>
              </div>

              <div className="bg-gold-gradient absolute -bottom-4 -left-4 -z-10 h-full w-full rounded-2xl opacity-50 blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== Trust strip ==================== */}
      <section className="border-y border-border bg-warm" data-testid="trust-strip">
        <div className="mx-auto max-w-7xl px-6 py-8">
          <div className="text-center text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Our Partners and Associates
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
            {TRUST_LOGOS.map((l) => (
              <span
                key={l}
                className="font-display text-lg font-bold tracking-tight text-muted-foreground/70 hover:text-foreground transition-colors"
              >
                {l}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== Course Categories ==================== */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <div className="chip-gold mb-3">Explore by category</div>
              <h2 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
                Find your <span className="gold-text-gradient">specialisation</span>.
              </h2>
              <p className="mt-3 max-w-2xl text-base text-muted-foreground">
                Four flagship tracks, each with Certificate (6 mo), Diploma (1 yr) and Degree (2 yr) programmes.
              </p>
            </div>
            <Link to="/courses">
              <Button variant="ghost" className="rounded-full font-semibold text-primary hover:bg-primary/10">
                See all programmes <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {CATEGORIES.map((c) => (
              <Link
                key={c.title}
                to="/courses"
                className="group relative overflow-hidden rounded-xl border border-border bg-white hard-shadow-sm hover-lift"
                data-testid={`category-${c.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <div className="aspect-course relative overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="bg-gold-gradient mb-2 inline-flex h-9 w-9 items-center justify-center rounded-lg">
                      <c.icon className="h-4 w-4 text-white" />
                    </div>
                    <div className="font-display text-xl font-bold text-white">
                      {c.title}
                    </div>
                    <div className="mt-0.5 text-xs text-white/80">{c.courses}</div>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm text-muted-foreground">{c.accent}</p>
                  <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-primary">
                    Browse programmes
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== Featured Courses ==================== */}
      <section className="border-b border-border bg-warm">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <div className="chip-gold mb-3">Most popular</div>
              <h2 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
                Featured <span className="gold-text-gradient">programmes</span>.
              </h2>
              <p className="mt-3 max-w-2xl text-base text-muted-foreground">
                Hand-picked courses our learners love — verified ratings, 30-day money-back.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURED_COURSES.map((c) => (
              <Link
                key={c.title}
                to="/courses"
                className="group flex flex-col overflow-hidden rounded-xl border border-border bg-white hard-shadow-sm hover-lift"
                data-testid={`featured-${c.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <div className="aspect-course relative overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute left-3 top-3">
                    <span className="chip-gold backdrop-blur-md">{c.badge}</span>
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="font-display text-lg font-bold leading-snug tracking-tight">
                    {c.title}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{c.instructor}</div>

                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <span className="flex items-center gap-1 font-bold text-foreground">
                      <Star className="h-3.5 w-3.5 fill-primary text-primary" />
                      {c.rating}
                    </span>
                    <span className="text-muted-foreground">({c.students.toLocaleString()})</span>
                  </div>

                  <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{c.duration}</span>
                    <span className="h-1 w-1 rounded-full bg-border" />
                    <span>{c.level}</span>
                  </div>

                  <div className="mt-auto flex items-end justify-between pt-5">
                    <div className="font-display text-2xl font-bold tracking-tight">
                      {c.price}
                    </div>
                    <span className="text-sm font-semibold text-primary">
                      Enrol →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== Mowanzi Coin section ==================== */}
      <section className="relative overflow-hidden border-b border-border bg-charcoal-gradient text-white">
        <div className="bg-dot-pattern absolute inset-0 opacity-30"></div>
        <div className="relative mx-auto max-w-7xl px-6 py-24">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <div className="chip-gold mb-4">
                <Coins className="h-3.5 w-3.5" />
                The Mowanzi reward economy
              </div>
              <h2 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
                Most platforms charge you to learn.
                <br />
                <span className="gold-text-gradient">Mowanzi pays you.</span>
              </h2>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/75">
                Every module, course and learning streak earns you <strong className="text-white">Mowanzi (MWZ) coins</strong> —
                spendable on books, hardware, premium AI tutor sessions, hackathon tickets and
                even tuition discounts.
              </p>

              <div className="mt-8 space-y-3">
                <EarnLine icon={Sparkles} label="Sign up" reward="+500 MWZ" />
                <EarnLine icon={BookOpen} label="Every module completed" reward="+1,000 MWZ" />
                <EarnLine icon={Trophy} label="Every course completed" reward="+5,000 MWZ bonus" />
                <EarnLine icon={Flame} label="Daily learning streak" reward="+50 MWZ / day" />
              </div>

              <Link to="/login" className="mt-10 inline-block">
                <Button
                  size="lg"
                  className="btn-gold rounded-full px-7 py-6 text-base font-semibold"
                  data-testid="cta-mwz-signup"
                >
                  Claim your 500 MWZ welcome bonus
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="lg:col-span-6">
              <div className="relative">
                {/* MWZ wallet card */}
                <div className="relative rounded-2xl border border-white/15 bg-white/[0.04] p-7 backdrop-blur-md">
                  <div className="bg-gold-gradient absolute -top-6 left-6 flex h-12 w-12 items-center justify-center rounded-xl shadow-lg">
                    <Coins className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-16">
                    <div className="mono text-[10px] uppercase tracking-widest text-white/60">
                      Mowanzi Wallet
                    </div>
                    <div className="mt-1 flex items-baseline gap-2">
                      <div className="font-display text-5xl font-bold tracking-tight text-white">
                        6,500
                      </div>
                      <div className="mono text-base font-bold text-primary">MWZ</div>
                    </div>
                    <div className="mono mt-2 text-[10px] uppercase tracking-widest text-white/60">
                      Aria Mokoena · streak day 12
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-3 gap-3">
                    <MiniStat label="Modules" value="6" />
                    <MiniStat label="Courses" value="1" />
                    <MiniStat label="Streak" value="12d" />
                  </div>

                  <div className="mt-6 border-t border-white/10 pt-4">
                    <div className="mono mb-3 text-[10px] uppercase tracking-widest text-white/60">
                      Recent earnings
                    </div>
                    <div className="space-y-2 text-sm">
                      <RowL desc="Course complete · Blockchain Fundamentals" amt="+5,000" />
                      <RowL desc="Module complete · Cryptographic Foundations" amt="+1,000" />
                      <RowL desc="Module complete · Consensus Mechanisms" amt="+1,000" />
                      <RowL desc="Daily streak · day 12" amt="+50" />
                    </div>
                  </div>
                </div>

                <div className="bg-gold-gradient absolute -bottom-3 -right-3 hidden rounded-xl p-4 shadow-2xl md:block">
                  <ShoppingBag className="mb-2 h-5 w-5 text-white" />
                  <div className="font-display text-sm font-bold text-white">
                    Spend on
                  </div>
                  <div className="text-xs text-white/90">
                    Books · Hardware · Hoodies
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== Roles ==================== */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12">
            <div className="chip-gold mb-3">Three personas, one platform</div>
            <h2 className="font-display max-w-3xl text-4xl font-bold tracking-tight sm:text-5xl">
              Built for <span className="gold-text-gradient">learners, educators</span> and <span className="gold-text-gradient">institutions</span>.
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <RoleCard
              label="Student"
              tagline="Earn while you learn."
              gradient="from-amber-50 to-amber-100/40"
              icon={GraduationCap}
              points={[
                "Personal academic wallet & MWZ rewards",
                "NFT certificates with QR verification",
                "AI Tutor on every lesson",
                "Streaks, badges, leaderboard",
              ]}
            />
            <RoleCard
              label="Teacher"
              tagline="Author. Teach. Measure."
              gradient="from-slate-50 to-slate-100/60"
              icon={BookOpen}
              points={[
                "Author courses, modules & quizzes",
                "Live gradebook across cohorts",
                "Issue verified micro-credentials",
                "Track at-risk students",
              ]}
            />
            <RoleCard
              label="Administrator"
              tagline="Govern at scale."
              gradient="from-zinc-50 to-zinc-100/60"
              icon={ShieldCheck}
              points={[
                "Platform-wide analytics & revenue",
                "User management & permissions",
                "Immutable on-chain audit trail",
                "Catalogue & store oversight",
              ]}
            />
          </div>
        </div>
      </section>

      {/* ==================== Testimonials ==================== */}
      <section className="border-b border-border bg-warm">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 text-center">
            <div className="chip-gold mb-3">Learner stories</div>
            <h2 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
              Graduates building <span className="gold-text-gradient">the future of Africa</span>.
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="flex flex-col rounded-xl border border-border bg-white p-7 hard-shadow-sm hover-lift"
                data-testid={`testimonial-${i}`}
              >
                <div className="mb-4 flex">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                <p className="flex-1 text-foreground/85 leading-relaxed">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-6 flex items-center gap-3 border-t border-border pt-4">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="h-11 w-11 rounded-full border-2 border-primary/30 object-cover"
                  />
                  <div>
                    <div className="font-semibold text-foreground">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== Verification CTA ==================== */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6">
              <div className="chip-gold mb-3">Public verification portal</div>
              <h2 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
                Every credential is <span className="gold-text-gradient">publicly verifiable</span>.
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
                Employers, governments and partner institutions can verify any
                Mowanzi Academy credential in under three seconds — no account,
                no fee. Just paste the cert hash.
              </p>
              <Link to="/verify" className="mt-7 inline-block">
                <Button
                  size="lg"
                  className="btn-gold rounded-full px-7 py-6 text-base font-semibold"
                  data-testid="cta-verify-section"
                >
                  <ShieldCheck className="mr-2 h-5 w-5" />
                  Open verification portal
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-xl border border-border bg-foreground p-6 text-white hard-shadow">
                <div className="mono mb-3 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/60">
                  <Globe className="h-3 w-3" />
                  Audit record · live
                </div>
                <pre className="overflow-x-auto rounded-lg border border-white/10 bg-white/[0.03] p-4 font-mono text-xs leading-relaxed text-white/90">{`{
  "event": "certificate_issued",
  "subject": "Aria Mokoena → Blockchain Fundamentals",
  "issued_at": "2026-02-14T09:42:18Z",
  "cert_hash": "0x9f3a7d1c4b8e6a2f5d9c1e8b4a7f2d6c…",
  "txn_hash":  "0x1c0b2e9c5a8e6f3d…",
  "network": "polygon (simulated)",
  "block": 51284917,
  "valid": true
}`}</pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== Final CTA ==================== */}
      <section className="relative overflow-hidden border-b border-border bg-foreground py-24 text-white">
        <div className="bg-grid-dense absolute inset-0 opacity-30" />
        <div className="bg-gold-gradient absolute -right-32 -top-32 h-96 w-96 rounded-full opacity-20 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <div className="chip-gold mx-auto mb-4">Ready to begin?</div>
          <h2 className="font-display text-4xl font-bold tracking-tight sm:text-6xl">
            Your <span className="gold-text-gradient">lifelong learning identity</span>,
            <br /> anchored on-chain.
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-white/80">
            Three demo accounts available — student, teacher and administrator.
            All passwords <code className="mono rounded bg-white/10 px-2 py-0.5 text-primary">Demo@123</code>.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/login">
              <Button
                size="lg"
                className="btn-gold rounded-full px-8 py-6 text-base font-semibold"
                data-testid="footer-cta-signup"
              >
                Enter Mowanzi Academy <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/verify">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-2 border-white/30 bg-white/5 px-8 py-6 text-base font-semibold text-white backdrop-blur-sm hover:bg-white/15 hover:text-white"
              >
                <ShieldCheck className="mr-2 h-5 w-5" /> Verify a certificate
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// ----- Sub-components ----------------------------------------------------

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display text-3xl font-bold text-white">{n}</div>
      <div className="mt-1 text-xs uppercase tracking-wider text-white/60">{label}</div>
    </div>
  );
}

function EarnLine({ icon: Icon, label, reward }) {
  return (
    <div className="flex items-center justify-between border-b border-white/10 pb-3 last:border-b-0">
      <div className="flex items-center gap-3">
        <div className="bg-gold-gradient flex h-9 w-9 items-center justify-center rounded-lg">
          <Icon className="h-4 w-4 text-white" />
        </div>
        <span className="text-white/90">{label}</span>
      </div>
      <span className="mono text-sm font-bold text-primary">{reward}</span>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.04] p-3 text-center">
      <div className="mono text-[10px] uppercase tracking-widest text-white/60">
        {label}
      </div>
      <div className="font-display mt-1 text-xl font-bold tracking-tight text-white">
        {value}
      </div>
    </div>
  );
}

function RowL({ desc, amt }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="truncate text-white/70">{desc}</span>
      <span className="mono font-mono font-semibold text-primary">{amt}</span>
    </div>
  );
}

function RoleCard({ label, tagline, gradient, icon: Icon, points }) {
  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-border bg-gradient-to-br ${gradient} p-7 hover-lift hard-shadow-sm`}
      data-testid={`role-${label.toLowerCase()}`}
    >
      <div className="bg-gold-gradient mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl">
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div className="mono mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className="font-display mb-5 text-2xl font-bold tracking-tight">
        {tagline}
      </div>
      <ul className="space-y-3 text-sm">
        {points.map((p, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span className="text-foreground/85">{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
